import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["pricingDetails", "userGeolocation", "namespace", "rioConfig", "useForter"],
  _excluded2 = ["displayServerErrors"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { Component, ErrorType, SharedCommandNames, StatusTypes, Namespace, getPriceQuoteItems, getIsUpgradeEligible } from '@chegg/commerce-utils';
import { isOrderManagementAction } from './FetchActionUtil';
import { paymentHeading, PRICE_PLUS_TAX_COUNTRIES } from '../constants/appConstants';
import { reactiveAltProductData } from '../lib/ApolloSchema/reactiveVars';

/**
 * Component errors are different than StatusUpdate errors.  When DCO components have errors,
 * they send back an ErrorObject.
 * transformErrorObjectToStatusUpdate converts ErrorObject to a statusUpdate so
 * that Host applications can consume it, if necessary.
 *
 * @param error ErrorObject
 */
export var transformErrorObjectToStatusUpdate = function transformErrorObjectToStatusUpdate(error) {
  var componentName = error.componentName,
    errorMessage = error.errorMessage;
  return [{
    status: StatusTypes.ERROR,
    command: SharedCommandNames.INITIALIZE,
    payload: {
      component: componentName,
      context: Component.ONE_CLICK,
      errors: [{
        message: errorMessage,
        type: ErrorType.SERVER
      }]
    }
  }];
};
export var transformAppPropsToOneClickProps = function transformAppPropsToOneClickProps(props) {
  var _eligibility$networkI, _eligibility$taxQuote, _products$sort;
  var _setDefaultOneClickCo = setDefaultOneClickConfigs(props),
    pricingDetails = _setDefaultOneClickCo.pricingDetails,
    userGeolocation = _setDefaultOneClickCo.userGeolocation,
    _setDefaultOneClickCo2 = _setDefaultOneClickCo.namespace,
    namespace = _setDefaultOneClickCo2 === void 0 ? Namespace.ONE_CLICK : _setDefaultOneClickCo2,
    rioConfig = _setDefaultOneClickCo.rioConfig,
    useForter = _setDefaultOneClickCo.useForter,
    rest = _objectWithoutProperties(_setDefaultOneClickCo, _excluded);
  if (!pricingDetails.data) return null;
  var _ref = pricingDetails.data.payload,
    authorization = _ref.authorization,
    action = _ref.action,
    orderLineId = _ref.orderLineId,
    externalId = _ref.externalId,
    hostAnnotations = _ref.hostAnnotations,
    orderDetails = _ref.orderDetails,
    mopDetails = _ref.mopDetails,
    items = _ref.items,
    productType = _ref.productType,
    price = _ref.price,
    eligibility = _ref.eligibility,
    taxInfo = _ref.taxInfo,
    products = _ref.products;
  var isVpnDetected = ((_eligibility$networkI = eligibility.networkInfo) === null || _eligibility$networkI === void 0 ? void 0 : _eligibility$networkI.isAnonymous) || false;
  var country = eligibility.countryCode || (userGeolocation === null || userGeolocation === void 0 ? void 0 : userGeolocation.countryCode);

  // TODO remove the ignore and add some tests
  /* istanbul ignore next */
  var priceTotalWithTaxCurrency = PRICE_PLUS_TAX_COUNTRIES.includes(country) ?
  //@ts-ignore - taxQuote exists in response, but taxInfo is in the interface
  ((_eligibility$taxQuote = eligibility.taxQuote) === null || _eligibility$taxQuote === void 0 ? void 0 : _eligibility$taxQuote.priceTotalWithTaxCurrency) || (taxInfo === null || taxInfo === void 0 ? void 0 : taxInfo.priceTotalWithTaxCurrency) : price;

  // sort products so that the product associated with the next interaction response comes first
  var sortedProducts = (_products$sort = products === null || products === void 0 ? void 0 : products.sort(function (_ref2) {
    var productFamily = _ref2.productFamily;
    return productFamily === productType ? -1 : 1;
  })) !== null && _products$sort !== void 0 ? _products$sort : [];
  var _ref3 = sortedProducts,
    _ref4 = _slicedToArray(_ref3, 2),
    primaryProduct = _ref4[0],
    altPricingData = _ref4[1];
  var isMultimonthResubscribe = !!((products === null || products === void 0 ? void 0 : products.length) === 1 && getPriceQuoteItems(primaryProduct).length > 1);
  var eligibleForCSPUpgrade = getIsUpgradeEligible({
    action: action,
    productType: productType,
    products: products,
    countryCode: country
  });
  var allowPlanSelection = props.allowPlanSelection || eligibleForCSPUpgrade;
  var _items$ = items[0],
    catalogItemId = _items$.catalogItemId,
    pricingId = _items$.pricingId,
    annotation = _items$.annotation,
    catalogItemSource = _items$.catalogItemSource;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (allowPlanSelection && altPricingData) {
    var _altPricingData$taxQu, _altPricingData$eligi, _altPricingData$eligi2, _altPricingData$eligi3;
    // Test coverage for No Tax, tax countries are not currently included in upgrade
    /* istanbul ignore next */
    reactiveAltProductData({
      altItemData: getItemData(altPricingData, catalogItemSource),
      //@ts-ignore - taxQuote exists in response, but taxInfo is in the interface
      altTaxInfo: altPricingData.taxQuote,
      // No tax is covered, tax countries not implemented yet
      altPriceTotalWithTaxCurrency: PRICE_PLUS_TAX_COUNTRIES.includes(country) ? //@ts-ignore - taxQuote exists in response, but taxInfo is in the interface
      (_altPricingData$taxQu = altPricingData.taxQuote) === null || _altPricingData$taxQu === void 0 ? void 0 : _altPricingData$taxQu.priceTotalWithTaxCurrency : (_altPricingData$eligi = altPricingData.eligibility.price) === null || _altPricingData$eligi === void 0 ? void 0 : _altPricingData$eligi.priceWithCurrency,
      altPrice: ((_altPricingData$eligi2 = altPricingData.eligibility.price) === null || _altPricingData$eligi2 === void 0 ? void 0 : _altPricingData$eligi2.priceWithCurrency) || '',
      altPriceWithoutCurrency: Number((_altPricingData$eligi3 = altPricingData.eligibility.price) === null || _altPricingData$eligi3 === void 0 ? void 0 : _altPricingData$eligi3.price)
    });
  }
  var userUuid = pricingDetails.data.uuid;
  var orderManagementProps = getOrderManagementProps({
    action: action,
    orderDetails: orderDetails,
    mopDetails: mopDetails
  });
  return _objectSpread(_objectSpread({}, rest), {}, {
    namespace: rioConfig ? Namespace.ONE_CLICK : namespace,
    itemData: [{
      catalogItemId: catalogItemId,
      productType: productType,
      pricingId: catalogItemSource === 'RECURLY' ? getCurrencyCode(orderDetails, price) : pricingId,
      catalogItemSource: catalogItemSource !== null && catalogItemSource !== void 0 ? catalogItemSource : 'NETSUITE',
      quantity: 1,
      annotation: annotation
    }],
    orderLineId: orderLineId,
    externalId: externalId,
    useForter: setUseForter(useForter),
    price: getPriceWithCurrency(orderDetails, price, eligibility),
    priceWithoutCurrency: getPriceWithoutCurrency(orderDetails, price),
    currencyCode: getCurrencyCode(orderDetails, price),
    priceTotalWithTaxCurrency: priceTotalWithTaxCurrency,
    orderDetails: orderDetails,
    authorization: authorization,
    action: action,
    hostAnnotations: hostAnnotations,
    userUuid: userUuid,
    isVpnDetected: isVpnDetected,
    country: country,
    products: products || [],
    isMultimonthResubscribe: isMultimonthResubscribe,
    eligibleForCSPUpgrade: eligibleForCSPUpgrade,
    allowPlanSelection: allowPlanSelection,
    altPricingData: altPricingData
  }, orderManagementProps);
};
export var getOrderManagementProps = function getOrderManagementProps(_ref5) {
  var action = _ref5.action,
    orderDetails = _ref5.orderDetails,
    mopDetails = _ref5.mopDetails;
  if (isOrderManagementAction(action)) {
    if (orderDetails && mopDetails) {
      return {
        renewalDate: orderDetails.renewalDate || '',
        cardType: mopDetails.type,
        last4: mopDetails.last4,
        description: mopDetails.description,
        paymentAccountType: mopDetails.paymentAccountType,
        vpa: mopDetails.vpa
      };
    }
  }
  return {};
};
export var setUseForter = function setUseForter(useForter) {
  return useForter === true || useForter === undefined;
};
export var getPriceWithCurrency = function getPriceWithCurrency(orderDetails, price, eligibility) {
  var _eligibility$eligibil;
  return (orderDetails === null || orderDetails === void 0 ? void 0 : orderDetails.renewalPriceWithCurrency) || (price === null || price === void 0 ? void 0 : price.priceWithCurrency) || (eligibility === null || eligibility === void 0 || (_eligibility$eligibil = eligibility.eligibility.price) === null || _eligibility$eligibil === void 0 ? void 0 : _eligibility$eligibil.priceWithCurrency) || '';
};
export var getPriceWithoutCurrency = function getPriceWithoutCurrency(orderDetails, price) {
  return (orderDetails === null || orderDetails === void 0 ? void 0 : orderDetails.renewalPrice) || Number(price === null || price === void 0 ? void 0 : price.price);
};
/**
 * Returns the pre-discounted price, e.g. the recurring monthly price after a coupon is applied
 */
export var getPriceWithoutDiscount = function getPriceWithoutDiscount(_ref6) {
  var country = _ref6.country,
    price = _ref6.price,
    priceTotalWithTaxCurrency = _ref6.priceTotalWithTaxCurrency;
  return PRICE_PLUS_TAX_COUNTRIES.includes(country) && priceTotalWithTaxCurrency ? priceTotalWithTaxCurrency : price;
};
export function getPrice(_ref7) {
  var country = _ref7.country,
    price = _ref7.price,
    discountedPrice = _ref7.discountedPrice,
    priceTotalWithTaxCurrency = _ref7.priceTotalWithTaxCurrency;
  if (PRICE_PLUS_TAX_COUNTRIES.includes(country)) {
    return priceTotalWithTaxCurrency || discountedPrice || price;
  }
  return discountedPrice || price;
}
export var getCurrencyCode = function getCurrencyCode(orderDetails, price) {
  var _orderDetails$currenc;
  return (orderDetails === null || orderDetails === void 0 || (_orderDetails$currenc = orderDetails.currencyDetails) === null || _orderDetails$currenc === void 0 ? void 0 : _orderDetails$currenc.currencyCode) || (price === null || price === void 0 ? void 0 : price.currency) || '';
};

/**
 * Quick helper to set any default params to one click.
 * Can build something more robust if we end up having many configs to default.
 * @param props The props passed into one click on runtime
 */
export var setDefaultOneClickConfigs = function setDefaultOneClickConfigs(_ref8) {
  var _ref8$displayServerEr = _ref8.displayServerErrors,
    displayServerErrors = _ref8$displayServerEr === void 0 ? true : _ref8$displayServerEr,
    rest = _objectWithoutProperties(_ref8, _excluded2);
  return _objectSpread(_objectSpread({}, rest), {}, {
    displayServerErrors: displayServerErrors
  });
};
export var getItemData = function getItemData(_ref9, catalogItemSource) {
  var _currencyDetails$curr;
  var _ref9$eligibility = _ref9.eligibility,
    price = _ref9$eligibility.price,
    annotation = _ref9$eligibility.annotation,
    currencyDetails = _ref9$eligibility.currencyDetails,
    productFamily = _ref9.productFamily;
  var pricingId = price === null || price === void 0 ? void 0 : price.pricingLogId;
  return [{
    catalogItemId: price === null || price === void 0 ? void 0 : price.itemDetails.id,
    productType: productFamily,
    pricingId: catalogItemSource === 'RECURLY' ? (_currencyDetails$curr = currencyDetails === null || currencyDetails === void 0 ? void 0 : currencyDetails.currencyCode) !== null && _currencyDetails$curr !== void 0 ? _currencyDetails$curr : pricingId : pricingId,
    catalogItemSource: catalogItemSource !== null && catalogItemSource !== void 0 ? catalogItemSource : 'NETSUITE',
    quantity: 1,
    annotation: annotation
  }];
};
export var getUniqueIDLookUp = function getUniqueIDLookUp(fields) {
  var uniqueIdLookup = fields === null || fields === void 0 ? void 0 : fields.reduce(function (acc, field) {
    acc[field.uniqueId] = field.text;
    return acc;
  }, {});
  return {
    getHeadingTexts: function getHeadingTexts() {
      return {
        addPaymentMethodHeading: uniqueIdLookup === null || uniqueIdLookup === void 0 ? void 0 : uniqueIdLookup[paymentHeading.ADD_PAYMENT_METHOD_HEADING],
        editPaymentMethodHeading: uniqueIdLookup === null || uniqueIdLookup === void 0 ? void 0 : uniqueIdLookup[paymentHeading.EDIT_PAYMENT_METHOD_HEADING],
        cancelAnytime: uniqueIdLookup === null || uniqueIdLookup === void 0 ? void 0 : uniqueIdLookup[paymentHeading.CANCEL_ANYTIME]
      };
    },
    getValue: function getValue(key) {
      return uniqueIdLookup === null || uniqueIdLookup === void 0 ? void 0 : uniqueIdLookup[key];
    }
  };
};


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/one-click/dist/esm/utils/TransformUtils.js
// module id = 334
// module chunks = 11